import React, { useState, useRef, useEffect } from "react";
import {
  Box,
  Typography,
  TextField,
  Checkbox,
  FormControlLabel,
  IconButton,
  CircularProgress,
  Tooltip,
  styled,
  MenuItem,
  Select,
} from "@mui/material";
import {
  Settings as SettingsIcon,
  Close as CloseIcon,
  ArrowBack as ArrowBackIcon,
  Delete as DeleteIcon,
  Add as AddIcon,
  AutoFixHigh as SparkleIcon,
  CloudUpload as CloudUploadIcon,
} from "@mui/icons-material";

// Types
import {
  ScriptWithImageItem,
  CharacterSerializer,
  GenerateCharacterPayload,
  UploadCharacterPayload,
  VideoModelCatalog,
} from "./types/MeGenTypes";

// Utils
import { getOutputStaticUrl } from "./MeGenInterface";
import { CHARACTER_STYLE_PORTRAIT } from "./common/megenDefaults";
import {
  effectiveVideoModel,
  enabledVideoModels,
  findVideoModel,
  isLipsyncVideoModel,
  resolveStoredVideoModel,
  videoDefaultLabel,
  videoModelRequiresAnimPrompt,
  videoModelSupportsCharacter,
} from "./common/videoModelCatalog";

const STYLES = [
  { label: "Auto", value: "<auto>" },
  { label: "Anime", value: "<anime>" },
  { label: "3D Game", value: "<3d game>" },
  { label: "Photography", value: "<photography>" },
  { label: "Portrait", value: CHARACTER_STYLE_PORTRAIT },
  { label: "Oil Painting", value: "<oil painting>" },
  { label: "Sketch", value: "<sketch>" },
  { label: "Chinese", value: "<chinese painting>" },
  { label: "Watercolor", value: "<water color>" },
];

// ─── Styled Components ───────────────────────────────────────────────────────

const FormLabel = styled(Typography)({
  fontSize: "0.875rem",
  fontWeight: 500,
  color: "#64748b",
  marginBottom: "0.5rem",
});

const StyledTextField = styled(TextField)({
  "& .MuiOutlinedInput-root": {
    backgroundColor: "white",
    borderRadius: "0.5rem",
    "& fieldset": { borderColor: "#e2e8f0" },
    "&:hover fieldset": { borderColor: "#cbd5e1" },
    "&.Mui-focused fieldset": { borderColor: "#f382a0", borderWidth: "1px" },
  },
  "& .MuiInputBase-input": {
    padding: "12px 14px",
    fontSize: "0.875rem",
    color: "#334155",
  },
});

// ─── Sub-types ───────────────────────────────────────────────────────────────

type ModalView = "list" | "generate";
type CreateTab = "generate" | "upload";

// ─── Character Manage Modal ───────────────────────────────────────────────────

const CharacterManageModal: React.FC<{
  open: boolean;
  seriesId: number | undefined;
  characterList: CharacterSerializer[];
  selectedCharacterId: number | null;
  isGenerating: boolean;
  onClose: () => void;
  onSelect: (id: number | null) => void;
  onDelete: (characterId: number) => void;
  onGenerate: (payload: GenerateCharacterPayload) => void;
  onUpload: (payload: UploadCharacterPayload) => void;
}> = (props) => {
  const [view, setView] = useState<ModalView>("list");
  const [tab, setTab] = useState<CreateTab>("generate");
  const [newChar, setNewChar] = useState({ name: "", prompt: "", style: CHARACTER_STYLE_PORTRAIT });
  const [deleteConfirmId, setDeleteConfirmId] = useState<number | null>(null);

  // ── Upload tab state ──
  const [uploadChar, setUploadChar] = useState({ name: "", description: "" });
  const [uploadFile, setUploadFile] = useState<File | null>(null);
  const [previewUrl, setPreviewUrl] = useState<string>("");
  const [isDragging, setIsDragging] = useState(false);


  const fileInputRef = useRef<HTMLInputElement>(null);
  const pendingUploadRef = useRef(false);

  useEffect(() => {
    if (!uploadFile) {
      setPreviewUrl("");
      return;
    }
    const url = URL.createObjectURL(uploadFile);
    setPreviewUrl(url);
    return () => URL.revokeObjectURL(url);
  }, [uploadFile]);

  useEffect(() => {
    if (pendingUploadRef.current && !props.isGenerating) {
      pendingUploadRef.current = false;
      setUploadChar({ name: "", description: "" });
      setUploadFile(null);
      setView("list");
    }
  }, [props.isGenerating]);

  if (!props.open) return null;

  const handleGenerate = () => {
    if (!props.seriesId || !newChar.name.trim() || !newChar.prompt.trim()) return;

    const payload: GenerateCharacterPayload = {
      series_id: props.seriesId,
      name: newChar.name.trim(),
      prompt: newChar.prompt.trim(),
      style: newChar.style,
    };

    props.onGenerate(payload);
    // reset form + กลับ list
    setNewChar({ name: "", prompt: "", style: CHARACTER_STYLE_PORTRAIT });
    setView("list");
  };

  const handleSelectFile = (file: File | null | undefined) => {
    if (!file) return;
    if (!file.type.startsWith("image/")) return;
    setUploadFile(file);
  };

  const handleRemoveFile = () => {
    setUploadFile(null);
    if (fileInputRef.current) fileInputRef.current.value = "";
  };

  const isUploadValid = !!props.seriesId && !!uploadChar.name.trim() && !!uploadFile;

  const handleUpload = () => {
    if (!isUploadValid || !props.seriesId || !uploadFile) return;

    const payload: UploadCharacterPayload = {
      series_id: props.seriesId,
      name: uploadChar.name.trim(),
      file: uploadFile,
      ...(uploadChar.description.trim()
        ? { description: uploadChar.description.trim() }
        : {}),
    };

    pendingUploadRef.current = true;
    props.onUpload(payload);
  };

  const handleClose = () => {
    setView("list");
    setTab("generate");
    setNewChar({ name: "", prompt: "", style: CHARACTER_STYLE_PORTRAIT });
    setUploadChar({ name: "", description: "" });
    setUploadFile(null);
    setDeleteConfirmId(null);
    props.onClose();
  };

  return (
    // Overlay
    <Box
      onClick={handleClose}
      sx={{
        position: "fixed",
        inset: 0,
        zIndex: 1300,
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        backgroundColor: "rgba(0,0,0,0.5)",
        p: 2,
      }}
    >
      {/* Modal box */}
      <Box
        onClick={(e) => e.stopPropagation()}
        sx={{
          backgroundColor: "white",
          borderRadius: "0.75rem",
          width: "100%",
          maxWidth: "800px",
          overflow: "hidden",
          boxShadow: "0 20px 40px rgba(0,0,0,0.2)",
        }}
      >
        {/* ─── Header ─────────────────────────────── */}
        <Box
          sx={{
            background: "linear-gradient(135deg, #4c1d95 0%, #6d28d9 100%)",
            px: 2,
            py: 1.5,
            display: "flex",
            alignItems: "center",
            gap: 1,
          }}
        >
          {/* Back button — แสดงเฉพาะ view === "generate" */}
          {view === "generate" && (
            <Box
              component="button"
              onClick={() => setView("list")}
              sx={{
                display: "flex",
                alignItems: "center",
                gap: 0.5,
                background: "transparent",
                border: "none",
                cursor: "pointer",
                color: "rgba(255,255,255,0.8)",
                fontSize: "0.75rem",
                p: 0,
                mr: 0.5,
                "&:hover": { color: "white" },
              }}
            >
              <ArrowBackIcon sx={{ fontSize: 16 }} />
              กลับ
            </Box>
          )}

          {view === "generate" && (
            <Box sx={{ width: "1px", height: "16px", background: "rgba(255,255,255,0.3)" }} />
          )}

          <Typography sx={{ flex: 1, fontSize: "0.925rem", fontWeight: 500, color: "white" }}>
            {view === "list" ? "จัดการ Character" : "เพิ่ม Character ใหม่"}
          </Typography>

          {/* ปิด modal */}
          <IconButton
            size="small"
            onClick={handleClose}
            sx={{
              color: "rgba(255,255,255,0.8)",
              p: 0.25,
              "&:hover": { color: "white", backgroundColor: "rgba(255,255,255,0.15)" },
              borderRadius: "50%",
            }}
          >
            <CloseIcon sx={{ fontSize: 16 }} />
          </IconButton>
        </Box>

        {/* ─── Body: List view ────────────────────── */}
        {view === "list" && (
          <Box sx={{ p: 2.5 }}>
            <Typography sx={{ fontSize: "0.6875rem", color: "#64748b", mb: 1.5 }}>
              คลิกเพื่อเลือก · Hover เพื่อลบ · กด + เพื่อสร้างใหม่
            </Typography>

            {/* Character grid */}
            <Box sx={{ display: "grid", gridTemplateColumns: "repeat(4, 1fr)", gap: 1, mb: 1 }}>
              {/* None option */}
              <Box
                onClick={() => props.onSelect(null)}
                sx={{
                  border:
                    props.selectedCharacterId === null
                      ? "1.5px solid #7c3aed"
                      : "1px solid #e2e8f0",
                  borderRadius: "0.625rem",
                  overflow: "hidden",
                  cursor: "pointer",
                  backgroundColor: props.selectedCharacterId === null ? "#f5f3ff" : "white",
                  "&:hover": { borderColor: "#a78bfa" },
                  transition: "all 0.15s",
                }}
              >
                <Box
                  sx={{
                    aspectRatio: "1/1",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    backgroundColor: "#f8fafc",
                  }}
                >
                  <CloseIcon sx={{ fontSize: 20, color: "#94a3b8" }} />
                </Box>
                <Box sx={{ px: 0.75, py: 0.5 }}>
                  <Typography
                    sx={{
                      fontSize: "0.925rem",
                      fontWeight: 500,
                      color: props.selectedCharacterId === null ? "#6d28d9" : "#64748b",
                    }}
                  >
                    None
                  </Typography>
                </Box>
              </Box>

              {/* Character cards */}
              {props.characterList.map((char) => {
                const isSelected = props.selectedCharacterId === char.id;
                const isDeleteConfirm = deleteConfirmId === char.id;
                const imgSrc = char.image_url ? getOutputStaticUrl(char.image_url) : null;

                return (
                  <Box
                    key={char.id}
                    onClick={() => {
                      if (isDeleteConfirm) return;
                      props.onSelect(char.id);
                    }}
                    sx={{
                      border: isSelected ? "1.5px solid #7c3aed" : "1px solid #e2e8f0",
                      borderRadius: "0.625rem",
                      overflow: "hidden",
                      cursor: "pointer",
                      backgroundColor: isSelected ? "#f5f3ff" : "white",
                      "&:hover .char-del-btn": { opacity: 1 },
                      "&:hover": { borderColor: isSelected ? "#7c3aed" : "#a78bfa" },
                      transition: "all 0.15s",
                      position: "relative",
                    }}
                  >
                    {/* Image */}
                    <Box
                      sx={{
                        aspectRatio: "1/1",
                        backgroundColor: "#f5f3ff",
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        position: "relative",
                        overflow: "hidden",
                      }}
                    >
                      {imgSrc ? (
                        <Box
                          component="img"
                          src={imgSrc}
                          alt={char.name}
                          sx={{ width: "100%", height: "100%", objectFit: "cover" }}
                        />
                      ) : (
                        <Typography sx={{ fontSize: "1.5rem" }}>👤</Typography>
                      )}

                      {/* Selected checkmark */}
                      {isSelected && (
                        <Box
                          sx={{
                            position: "absolute",
                            top: 4,
                            right: 4,
                            width: 16,
                            height: 16,
                            borderRadius: "50%",
                            backgroundColor: "#7c3aed",
                            border: "1.5px solid white",
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "center",
                          }}
                        >
                          <Box component="svg" viewBox="0 0 10 8" sx={{ width: 8, height: 8 }}>
                            <polyline
                              points="1,4 4,7 9,1"
                              fill="none"
                              stroke="white"
                              strokeWidth="2"
                            />
                          </Box>
                        </Box>
                      )}

                      {/* Delete button (hover) */}
                      {!isDeleteConfirm ? (
                        <Box
                          className="char-del-btn"
                          component="button"
                          onClick={(e: React.MouseEvent) => {
                            e.stopPropagation();
                            setDeleteConfirmId(char.id);
                          }}
                          sx={{
                            position: "absolute",
                            top: 4,
                            left: 4,
                            width: 18,
                            height: 18,
                            borderRadius: "50%",
                            background: "rgba(239,68,68,0.88)",
                            border: "none",
                            cursor: "pointer",
                            color: "white",
                            fontSize: "0.625rem",
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "center",
                            opacity: 0,
                            transition: "opacity 0.15s",
                          }}
                          title="ลบ Character นี้"
                        >
                          <CloseIcon sx={{ fontSize: 10 }} />
                        </Box>
                      ) : (
                        // Confirm delete overlay
                        <Box
                          sx={{
                            position: "absolute",
                            inset: 0,
                            backgroundColor: "rgba(239,68,68,0.88)",
                            display: "flex",
                            flexDirection: "column",
                            alignItems: "center",
                            justifyContent: "center",
                            gap: 0.5,
                          }}
                        >
                          <Typography
                            sx={{ fontSize: "0.925rem", color: "white", fontWeight: 600 }}
                          >
                            ยืนยันในการลบหรือไม่?
                          </Typography>
                          <Box sx={{ display: "flex", gap: 0.5 }}>
                            <Box
                              component="button"
                              onClick={(e: React.MouseEvent) => {
                                e.stopPropagation();
                                props.onDelete(char.id);
                                setDeleteConfirmId(null);
                              }}
                              sx={{
                                padding: "2px 6px",
                                backgroundColor: "white",
                                border: "none",
                                borderRadius: "4px",
                                fontSize: "0.825rem",
                                fontWeight: 600,
                                color: "#dc2626",
                                cursor: "pointer",
                              }}
                            >
                              ลบ
                            </Box>
                            <Box
                              component="button"
                              onClick={(e: React.MouseEvent) => {
                                e.stopPropagation();
                                setDeleteConfirmId(null);
                              }}
                              sx={{
                                padding: "2px 6px",
                                backgroundColor: "transparent",
                                border: "1px solid rgba(255,255,255,0.6)",
                                borderRadius: "4px",
                                fontSize: "0.825rem",
                                color: "white",
                                cursor: "pointer",
                              }}
                            >
                              ยกเลิก
                            </Box>
                          </Box>
                        </Box>
                      )}
                    </Box>

                    {/* Info */}
                    <Box sx={{ px: 0.75, py: 0.5 }}>
                      <Typography
                        sx={{
                          fontSize: "0.925rem",
                          fontWeight: 500,
                          color: isSelected ? "#4c1d95" : "#1e293b",
                          overflow: "hidden",
                          textOverflow: "ellipsis",
                          whiteSpace: "nowrap",
                        }}
                      >
                        {char.name}
                      </Typography>
                      <Box
                        sx={{
                          display: "inline-block",
                          mt: 0.25,
                          px: 0.75,
                          py: "1px",
                          borderRadius: "9999px",
                          fontSize: "0.6rem",
                          fontWeight: 500,
                          backgroundColor: char.is_validated ? "#dcfce7" : "#fef3c7",
                          color: char.is_validated ? "#166534" : "#92400e",
                        }}
                      >
                        {char.is_validated ? "validated" : "unverified"}
                      </Box>
                    </Box>
                  </Box>
                );
              })}

              {/* + Generate new card */}
              <Box
                onClick={() => {
                  setTab("generate");
                  setView("generate");
                }}
                sx={{
                  border: "1px dashed #cbd5e1",
                  borderRadius: "0.625rem",
                  display: "flex",
                  flexDirection: "column",
                  alignItems: "center",
                  justifyContent: "center",
                  gap: 0.5,
                  cursor: "pointer",
                  aspectRatio: "1/1",
                  backgroundColor: "#f8fafc",
                  "&:hover": { borderColor: "#a78bfa", backgroundColor: "#f5f3ff" },
                  transition: "all 0.15s",
                }}
              >
                <AddIcon sx={{ fontSize: 20, color: "#a78bfa" }} />
                <Typography
                  sx={{ fontSize: "0.925rem", color: "#64748b", textAlign: "center", px: 0.5 }}
                >
                  Generate ใหม่
                </Typography>
              </Box>
            </Box>

            {/* Generating state overlay hint */}
            {props.isGenerating && (
              <Box
                sx={{
                  display: "flex",
                  alignItems: "center",
                  gap: 1,
                  p: 1,
                  backgroundColor: "#f5f3ff",
                  borderRadius: "0.5rem",
                  border: "0.5px solid #c4b5fd",
                }}
              >
                <CircularProgress size={14} sx={{ color: "#7c3aed" }} />
                <Typography sx={{ fontSize: "0.6875rem", color: "#6d28d9" }}>
                  กำลัง Generate...
                </Typography>
              </Box>
            )}
          </Box>
        )}

        {/* ─── Body: Create view (2 tabs) ─────────── */}
        {view === "generate" && (
          <Box>
            {/* Tab bar */}
            <Box
              sx={{
                display: "flex",
                gap: 0,
                px: 2.5,
                pt: 2,
                borderBottom: "1px solid #e9d5ff",
              }}
            >
              {([
                { key: "generate", label: "Generate" },
                { key: "upload", label: "Upload Character" },
              ] as { key: CreateTab; label: string }[]).map((t) => (
                <Box
                  key={t.key}
                  component="button"
                  onClick={() => setTab(t.key)}
                  disabled={props.isGenerating}
                  sx={{
                    px: 2,
                    py: 1,
                    background: "transparent",
                    border: "none",
                    borderBottom:
                      tab === t.key ? "2px solid #7c3aed" : "2px solid transparent",
                    color: tab === t.key ? "#6d28d9" : "#94a3b8",
                    fontSize: "0.75rem",
                    fontWeight: tab === t.key ? 600 : 500,
                    cursor: props.isGenerating ? "not-allowed" : "pointer",
                    "&:disabled": { opacity: 0.6 },
                    "&:hover:not(:disabled)": { color: "#6d28d9" },
                  }}
                >
                  {t.label}
                </Box>
              ))}
            </Box>

            {/* ── Tab: Generate ── */}
            {tab === "generate" && (
          <Box sx={{ p: 2.5, display: "flex", flexDirection: "column", gap: 1.5 }}>
            {/* Name */}
            <Box>
              <Typography
                sx={{ fontSize: "0.6875rem", fontWeight: 500, color: "#6d28d9", mb: 0.5 }}
              >
                Character Name{" "}
                <Box component="span" sx={{ color: "#dc2626" }}>
                  *
                </Box>
              </Typography>
              <TextField
                fullWidth
                size="small"
                placeholder="e.g. Dr. Somchai, Ancient Guardian..."
                value={newChar.name}
                onChange={(e) => {
                  const value = e.target.value;
                  setNewChar((prev) => ({ ...prev, name: value }));
                }}
                disabled={props.isGenerating}
                sx={{
                  "& .MuiOutlinedInput-root": {
                    backgroundColor: "white",
                    borderRadius: "0.5rem",
                    fontSize: "0.8125rem",
                    "& fieldset": { borderColor: "#c4b5fd" },
                    "&:hover fieldset": { borderColor: "#a78bfa" },
                    "&.Mui-focused fieldset": { borderColor: "#7c3aed", borderWidth: "1px" },
                  },
                }}
              />
            </Box>

            {/* Art Style */}
            <Box>
              <Typography
                sx={{ fontSize: "0.6875rem", fontWeight: 500, color: "#6d28d9", mb: 0.75 }}
              >
                Art Style
              </Typography>
              <Box sx={{ display: "grid", gridTemplateColumns: "repeat(3, 1fr)", gap: 0.5 }}>
                {STYLES.map((style) => (
                  <Box
                    key={style.value}
                    component="button"
                    onClick={() => setNewChar((prev) => ({ ...prev, style: style.value }))}
                    disabled={props.isGenerating}
                    sx={{
                      py: 0.5,
                      px: 0.75,
                      border:
                        newChar.style === style.value ? "1px solid #7c3aed" : "1px solid #e2e8f0",
                      borderRadius: "6px",
                      fontSize: "0.625rem",
                      fontWeight: 500,
                      cursor: "pointer",
                      backgroundColor: newChar.style === style.value ? "#7c3aed" : "white",
                      color: newChar.style === style.value ? "white" : "#64748b",
                      transition: "all 0.12s",
                      "&:hover:not(:disabled)": { borderColor: "#a78bfa" },
                      "&:disabled": { opacity: 0.5, cursor: "not-allowed" },
                    }}
                  >
                    {style.label}
                  </Box>
                ))}
              </Box>
            </Box>

            {/* Appearance Prompt */}
            <Box>
              <Typography
                sx={{ fontSize: "0.6875rem", fontWeight: 500, color: "#6d28d9", mb: 0.5 }}
              >
                Appearance Prompt{" "}
                <Box component="span" sx={{ color: "#dc2626" }}>
                  *
                </Box>
              </Typography>
              <TextField
                fullWidth
                multiline
                rows={3}
                placeholder="Describe how the character looks (e.g. A middle-aged man with glasses, wearing a lab coat...)"
                value={newChar.prompt}
                onChange={(e) => {
                  const value = e.target.value;
                  setNewChar((prev) => ({ ...prev, prompt: value }));
                }}
                disabled={props.isGenerating}
                sx={{
                  "& .MuiOutlinedInput-root": {
                    backgroundColor: "white",
                    borderRadius: "0.5rem",
                    fontSize: "0.8125rem",
                    padding: "6px 8px",
                    "& fieldset": { borderColor: "#c4b5fd" },
                    "&:hover fieldset": { borderColor: "#a78bfa" },
                    "&.Mui-focused fieldset": { borderColor: "#7c3aed", borderWidth: "1px" },
                  },
                  "& .MuiInputBase-input": { padding: "2px 4px", fontSize: "0.8125rem" },
                }}
              />
            </Box>

            {/* Generate button */}
            <Box
              component="button"
              onClick={handleGenerate}
              disabled={props.isGenerating || !newChar.name.trim() || !newChar.prompt.trim()}
              sx={{
                width: "100%",
                py: 1.25,
                background:
                  props.isGenerating || !newChar.name.trim() || !newChar.prompt.trim()
                    ? "#f1f5f9"
                    : "linear-gradient(135deg, #4c1d95 0%, #6d28d9 100%)",
                color:
                  props.isGenerating || !newChar.name.trim() || !newChar.prompt.trim()
                    ? "#94a3b8"
                    : "white",
                border: "none",
                borderRadius: "0.5rem",
                fontSize: "0.8125rem",
                fontWeight: 500,
                cursor:
                  props.isGenerating || !newChar.name.trim() || !newChar.prompt.trim()
                    ? "not-allowed"
                    : "pointer",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                gap: 1,
                transition: "all 0.15s",
              }}
            >
              {props.isGenerating ? (
                <>
                  <CircularProgress size={14} sx={{ color: "#a78bfa" }} />
                  Generating...
                </>
              ) : (
                <>
                  <SparkleIcon sx={{ fontSize: 16 }} />
                  Generate
                </>
              )}
            </Box>
          </Box>
            )}

            {/* ── Tab: Upload Character ── */}
            {tab === "upload" && (
              <Box sx={{ p: 2.5, display: "flex", flexDirection: "column", gap: 1.5 }}>
                {/* Name (required) */}
                <Box>
                  <Typography
                    sx={{ fontSize: "0.6875rem", fontWeight: 500, color: "#6d28d9", mb: 0.5 }}
                  >
                    Character Name{" "}
                    <Box component="span" sx={{ color: "#dc2626" }}>
                      *
                    </Box>
                  </Typography>
                  <TextField
                    fullWidth
                    size="small"
                    placeholder="e.g. Dr. Somchai, Ancient Guardian..."
                    value={uploadChar.name}
                    onChange={(e) => {
                      const value = e.target.value;
                      setUploadChar((prev) => ({ ...prev, name: value }));
                    }}
                    disabled={props.isGenerating}
                    sx={{
                      "& .MuiOutlinedInput-root": {
                        backgroundColor: "white",
                        borderRadius: "0.5rem",
                        fontSize: "0.8125rem",
                        "& fieldset": { borderColor: "#c4b5fd" },
                        "&:hover fieldset": { borderColor: "#a78bfa" },
                        "&.Mui-focused fieldset": { borderColor: "#7c3aed", borderWidth: "1px" },
                      },
                    }}
                  />
                </Box>

                {/* Image upload (drag & drop + preview) */}
                <Box>
                  <Typography
                    sx={{ fontSize: "0.6875rem", fontWeight: 500, color: "#6d28d9", mb: 0.5 }}
                  >
                    Character Image{" "}
                    <Box component="span" sx={{ color: "#dc2626" }}>
                      *
                    </Box>
                  </Typography>

                  {/* hidden file input */}
                  <input
                    ref={fileInputRef}
                    type="file"
                    accept="image/*"
                    style={{ display: "none" }}
                    onChange={(e) => handleSelectFile(e.target.files?.[0])}
                  />

                  {!uploadFile ? (
                    <Box
                      onClick={() => !props.isGenerating && fileInputRef.current?.click()}
                      onDragOver={(e) => {
                        e.preventDefault();
                        if (!props.isGenerating) setIsDragging(true);
                      }}
                      onDragLeave={(e) => {
                        e.preventDefault();
                        setIsDragging(false);
                      }}
                      onDrop={(e) => {
                        e.preventDefault();
                        setIsDragging(false);
                        if (props.isGenerating) return;
                        handleSelectFile(e.dataTransfer.files?.[0]);
                      }}
                      sx={{
                        display: "flex",
                        flexDirection: "column",
                        alignItems: "center",
                        justifyContent: "center",
                        gap: 0.75,
                        py: 3,
                        px: 2,
                        borderRadius: "0.5rem",
                        border: `1.5px dashed ${isDragging ? "#7c3aed" : "#c4b5fd"}`,
                        backgroundColor: isDragging ? "#f5f3ff" : "#faf5ff",
                        cursor: props.isGenerating ? "not-allowed" : "pointer",
                        opacity: props.isGenerating ? 0.6 : 1,
                        transition: "all 0.15s",
                        "&:hover": { borderColor: "#a78bfa", backgroundColor: "#f5f3ff" },
                      }}
                    >
                      <CloudUploadIcon sx={{ fontSize: 28, color: "#a78bfa" }} />
                      <Typography sx={{ fontSize: "0.75rem", color: "#6d28d9", fontWeight: 500 }}>
                        ลากรูปมาวาง หรือคลิกเพื่อเลือกไฟล์
                      </Typography>
                      <Typography sx={{ fontSize: "0.625rem", color: "#94a3b8" }}>
                        รองรับไฟล์รูปภาพ (PNG, JPG, WEBP)
                      </Typography>
                    </Box>
                  ) : (
                    /* Preview + remove */
                    <Box
                      sx={{
                        display: "flex",
                        gap: 1.25,
                        p: 1.25,
                        borderRadius: "0.5rem",
                        border: "0.5px solid #c4b5fd",
                        backgroundColor: "#f5f3ff",
                      }}
                    >
                      <Box
                        sx={{
                          width: 88,
                          height: 88,
                          borderRadius: "0.5rem",
                          overflow: "hidden",
                          flexShrink: 0,
                          border: "1.5px solid #c4b5fd",
                          backgroundColor: "#ede9fe",
                        }}
                      >
                        {previewUrl && (
                          <Box
                            component="img"
                            src={previewUrl}
                            alt={uploadFile.name}
                            sx={{ width: "100%", height: "100%", objectFit: "cover" }}
                          />
                        )}
                      </Box>

                      <Box sx={{ flex: 1, minWidth: 0, display: "flex", flexDirection: "column" }}>
                        <Typography
                          sx={{
                            fontSize: "0.75rem",
                            fontWeight: 500,
                            color: "#4c1d95",
                            overflow: "hidden",
                            textOverflow: "ellipsis",
                            whiteSpace: "nowrap",
                          }}
                        >
                          {uploadFile.name}
                        </Typography>
                        <Typography sx={{ fontSize: "0.625rem", color: "#6d28d9", mb: "auto" }}>
                          {(uploadFile.size / 1024).toFixed(0)} KB
                        </Typography>

                        <Box sx={{ display: "flex", gap: 0.75 }}>
                          <Box
                            component="button"
                            onClick={() => fileInputRef.current?.click()}
                            disabled={props.isGenerating}
                            sx={{
                              display: "flex",
                              alignItems: "center",
                              gap: 0.375,
                              px: 1,
                              py: 0.5,
                              fontSize: "0.625rem",
                              fontWeight: 500,
                              color: "#6d28d9",
                              backgroundColor: "white",
                              border: "0.5px solid #c4b5fd",
                              borderRadius: "6px",
                              cursor: "pointer",
                              "&:disabled": { opacity: 0.5, cursor: "not-allowed" },
                              "&:hover:not(:disabled)": { borderColor: "#7c3aed" },
                            }}
                          >
                            เปลี่ยนรูป
                          </Box>
                          <Box
                            component="button"
                            onClick={handleRemoveFile}
                            disabled={props.isGenerating}
                            sx={{
                              display: "flex",
                              alignItems: "center",
                              gap: 0.375,
                              px: 1,
                              py: 0.5,
                              fontSize: "0.625rem",
                              fontWeight: 500,
                              color: "#dc2626",
                              backgroundColor: "white",
                              border: "0.5px solid #fecaca",
                              borderRadius: "6px",
                              cursor: "pointer",
                              "&:disabled": { opacity: 0.5, cursor: "not-allowed" },
                              "&:hover:not(:disabled)": { backgroundColor: "#fef2f2" },
                            }}
                          >
                            <DeleteIcon sx={{ fontSize: 12 }} />
                            ลบรูป
                          </Box>
                        </Box>
                      </Box>
                    </Box>
                  )}
                </Box>

                {/* Appearance Prompt (optional → description) */}
                <Box>
                  <Typography
                    sx={{ fontSize: "0.6875rem", fontWeight: 500, color: "#6d28d9", mb: 0.5 }}
                  >
                    Appearance Prompt{" "}
                    <Box component="span" sx={{ color: "#94a3b8", fontWeight: 400 }}>
                      (optional)
                    </Box>
                  </Typography>
                  <TextField
                    fullWidth
                    multiline
                    rows={3}
                    placeholder="Describe how the character looks (optional)"
                    value={uploadChar.description}
                    onChange={(e) => {
                      const value = e.target.value;
                      setUploadChar((prev) => ({ ...prev, description: value }));
                    }}
                    disabled={props.isGenerating}
                    sx={{
                      "& .MuiOutlinedInput-root": {
                        backgroundColor: "white",
                        borderRadius: "0.5rem",
                        fontSize: "0.8125rem",
                        padding: "6px 8px",
                        "& fieldset": { borderColor: "#c4b5fd" },
                        "&:hover fieldset": { borderColor: "#a78bfa" },
                        "&.Mui-focused fieldset": { borderColor: "#7c3aed", borderWidth: "1px" },
                      },
                      "& .MuiInputBase-input": { padding: "2px 4px", fontSize: "0.8125rem" },
                    }}
                  />
                </Box>

                {/* Upload button */}
                <Box
                  component="button"
                  onClick={handleUpload}
                  disabled={props.isGenerating || !isUploadValid}
                  sx={{
                    width: "100%",
                    py: 1.25,
                    background:
                      props.isGenerating || !isUploadValid
                        ? "#f1f5f9"
                        : "linear-gradient(135deg, #4c1d95 0%, #6d28d9 100%)",
                    color: props.isGenerating || !isUploadValid ? "#94a3b8" : "white",
                    border: "none",
                    borderRadius: "0.5rem",
                    fontSize: "0.8125rem",
                    fontWeight: 500,
                    cursor: props.isGenerating || !isUploadValid ? "not-allowed" : "pointer",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    gap: 1,
                    transition: "all 0.15s",
                  }}
                >
                  {props.isGenerating ? (
                    <>
                      <CircularProgress size={14} sx={{ color: "#a78bfa" }} />
                      Uploading...
                    </>
                  ) : (
                    <>
                      <CloudUploadIcon sx={{ fontSize: 16 }} />
                      Upload Character
                    </>
                  )}
                </Box>
              </Box>
            )}
          </Box>
        )}
      </Box>
    </Box>
  );
};

// ─── Main Component ───────────────────────────────────────────────────────────

const CardMeGenSettingsSection: React.FC<{
  seriesId: number | undefined;
  item: ScriptWithImageItem;
  index: number;
  outputItem: any;
  isDisabled: boolean;
  isInputDisabled?: boolean;
  // Character props
  characterList: CharacterSerializer[];
  isCharacterGenerating: boolean;
  // catalog โมเดล video (GET /media/video-models) — null = ยังไม่โหลด/โหลดไม่สำเร็จ
  // list / default / alias / ความสามารถ มาจากที่นี่เท่านั้น ห้าม hardcode ชื่อโมเดลในไฟล์นี้
  videoModelCatalog?: VideoModelCatalog | null;
  // Callbacks
  updateScriptItem: (index: number, field: string, value: any) => void;
  onAnimPromptChange?: (value: string) => void;
  onGenerateCharacter: (payload: GenerateCharacterPayload) => void;
  onUploadCharacter: (payload: UploadCharacterPayload) => void;
  onDeleteCharacter: (characterId: number) => void;
}> = (props) => {
  const [showModal, setShowModal] = useState(false);

  // Clear Video Model เมื่อลบ Video Prompt — ไม่ stamp default ให้แล้ว (default เป็นของหลังบ้าน:
  // ไม่ส่ง key `video_model` = หลังบ้านเลือกเอง และ hash ของ "" เท่าเดิม ⇒ ไม่มีวิดีโอเก่า regen)
  // ทำงานเฉพาะตอน anim_prompt "เปลี่ยนสถานะ" (ว่าง <-> มี) เท่านั้น — ไม่เขียนทับตอน mount
  // เพื่อไม่ให้ video_model ที่ server ส่งมา (เช่น lip-sync จาก meLab) ถูกล้างเองแล้ว trigger auto-save
  const prevPromptStateRef = useRef<{ index: number; hasPrompt: boolean } | null>(null);
  useEffect(() => {
    const hasPrompt = !!props.item.anim_prompt?.trim();
    const prev = prevPromptStateRef.current;
    prevPromptStateRef.current = { index: props.index, hasPrompt };

    // ครั้งแรก (mount) หรือ component ถูก reuse กับ scene อื่น → แค่จำสถานะ ไม่แก้ค่า
    if (!prev || prev.index !== props.index || prev.hasPrompt === hasPrompt) return;
    if (hasPrompt || !props.item.video_model) return;

    // ตอบไม่ได้ (ไม่มี catalog / ไม่รู้จัก id) หรือโมเดลไม่ต้องใช้ prompt (lip-sync) → ห้ามล้างของ user
    const catalog = props.videoModelCatalog ?? null;
    const resolved = resolveStoredVideoModel(props.item.video_model, catalog);
    if (videoModelRequiresAnimPrompt(resolved, catalog) === true) {
      props.updateScriptItem(props.index, "video_model", "");
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [props.item.anim_prompt, props.index]);

  // ไม่แสดงเลยถ้าไม่มี image_url / anim_prompt / video_model
  if (!props.outputItem?.image_url && !props.item.anim_prompt && !props.item.video_model) return null;

  const isAnimPromptBlank = !props.item.anim_prompt?.trim();
  const selectedCharId = props.item.character_id ?? null;
  const selectedChar = props.characterList.find((c) => c.id === selectedCharId) ?? null;

  // ── Video Model (catalog หลังบ้าน) ──
  const videoModelCatalog = props.videoModelCatalog ?? null;
  // resolve alias เพื่อ "แสดงผล" เท่านั้น — ค่าดิบใน item ไม่แตะจนกว่า user จะเลือกเอง
  // (หลังบ้าน hash ค่าดิบ การเขียนทับ = regen วิดีโอเก่า)
  const displayVideoModel = resolveStoredVideoModel(props.item.video_model, videoModelCatalog);
  const enabledVideoModelList = enabledVideoModels(videoModelCatalog);
  // id ที่ item ถืออยู่แต่ไม่อยู่ใน list ที่เลือกได้ (ถูก disable ทีหลัง / legacy) — ต้อง render ไว้กัน MUI out-of-range
  const isOrphanVideoModel =
    !!displayVideoModel && !enabledVideoModelList.some((model) => model.id === displayVideoModel);
  // ไม่มี catalog = ตอบไม่ได้ว่าโมเดลถูกปิดจริงไหม (gen ยังทำงานปกติ) → ไม่ติดป้าย
  const videoOrphanSuffix = videoModelCatalog ? " (unavailable)" : "";
  // โมเดลที่หลังบ้านจะใช้จริงกับ scene นี้ — ต่างจาก displayVideoModel ตรงที่ถ้า user ไม่ได้เลือก
  // จะตกไปใช้ catalog.default ⇒ ตัดสินได้ถูกแม้ user เลือก "Use Global Default"
  const runningVideoModel = effectiveVideoModel(props.item, videoModelCatalog);

  // ── โมเดลนี้ต้องมี Video Prompt ไหม — ใช้ 2 แบบ ต่างกันที่ทิศทางของการ "เดาปลอดภัย" ──
  //
  // `=== true` สำหรับ *ข้อความเตือน*: ตอบไม่ได้ (null) → เงียบไว้ ดีกว่าเตือนมั่ว
  const needsAnimPrompt =
    isAnimPromptBlank && videoModelRequiresAnimPrompt(runningVideoModel, videoModelCatalog) === true;
  //
  // `!== false` สำหรับ *การล็อก UI*: ตอบไม่ได้ (null) → ล็อกไว้ก่อน
  // ต้องตรงกับกฎที่บล็อกตอนกด generate (common/videoModeIssues.ts) ไม่งั้นจอบอกว่าทำได้แต่กดแล้วเด้ง
  const promptRequiredForCharacter =
    isAnimPromptBlank &&
    videoModelRequiresAnimPrompt(runningVideoModel, videoModelCatalog) !== false;
  // เลือก character ไว้ แต่โมเดลที่จะใช้จริงไม่รองรับ ⇒ หลังบ้านทิ้ง character เงียบ ๆ ต้องบอก user
  // `=== false` เท่านั้น — null (ไม่มี catalog / ไม่รู้จัก id) แปลว่า "ตอบไม่ได้" ห้ามเตือนมั่ว
  const characterWillBeIgnored =
    !!selectedCharId && videoModelSupportsCharacter(runningVideoModel, videoModelCatalog) === false;

  return (
    <Box
      id="megen-settings-section-root"
      sx={{ mt: 2, pl: 1, pt: 1.5, borderTop: "1px dashed #ddd6fe" }}
    >
      {/* ─── Video Prompt from Image ─── */}
      <Box sx={{ mb: 2 }}>
        <FormLabel sx={{ color: "#7c3aed", fontWeight: 700 }}>Video Prompt from Image</FormLabel>
        <StyledTextField
          fullWidth
          multiline
          rows={6}
          placeholder="Describe the motion..."
          value={props.item.anim_prompt || ""}
          onChange={(e) => {
            props.updateScriptItem(props.index, "anim_prompt", e.target.value);
            if (e.target.value) props.onAnimPromptChange?.(e.target.value);
          }}
          disabled={props.isInputDisabled ?? props.isDisabled}
          sx={{
            "& .MuiOutlinedInput-root": { backgroundColor: "#faf5ff", padding: "4px 8px" },
            "& .MuiInputBase-input": { padding: "2px 4px" },
          }}
        />
      </Box>

      {/* ─── Scene Count ─── */}
      <Box sx={{ display: "flex", alignItems: "center", gap: 2, mb: 2 }}>
        <FormLabel
          sx={{ color: isAnimPromptBlank ? "#94a3b8" : "#7c3aed", fontWeight: 700, pt: 1, mb: 0 }}
        >
          Scene Count
        </FormLabel>
        <Box sx={{ display: "flex", gap: 1 }}>
          <FormControlLabel
            control={
              <Checkbox
                size="small"
                checked={(props.item.anim_mode || "single") === "single"}
                onChange={() => props.updateScriptItem(props.index, "anim_mode", "single")}
                sx={{ "&.Mui-checked": { color: "#7c3aed" } }}
                disabled={(props.isInputDisabled ?? props.isDisabled) || isAnimPromptBlank}
              />
            }
            label={<Typography sx={{ fontSize: "0.75rem" }}>Single Scene</Typography>}
          />
          <FormControlLabel
            control={
              <Checkbox
                size="small"
                checked={props.item.anim_mode === "multi"}
                onChange={() => props.updateScriptItem(props.index, "anim_mode", "multi")}
                sx={{ "&.Mui-checked": { color: "#7c3aed" } }}
                disabled={(props.isInputDisabled ?? props.isDisabled) || isAnimPromptBlank}
              />
            }
            label={<Typography sx={{ fontSize: "0.75rem" }}>Multi Scene</Typography>}
          />
        </Box>
      </Box>

      {/* ─── Character ─── */}
      <Box sx={{ borderTop: "0.5px solid #e9d5ff", pt: 1.5 }}>
        {/* Header row */}
        <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 1 }}>
          <FormLabel
            sx={{ color: isAnimPromptBlank ? "#94a3b8" : "#7c3aed", fontWeight: 700, mb: 0 }}
          >
            Character
          </FormLabel>
          <Box sx={{ flex: 1 }} />
          <Tooltip title="จัดการ Character">
            <Box component="span" sx={{ display: "inline-flex" }}>
              <Box
                component="button"
                onClick={() => !isAnimPromptBlank && !props.isDisabled && setShowModal(true)}
                disabled={isAnimPromptBlank || props.isDisabled}
                sx={{
                  backgroundColor: isAnimPromptBlank ? "#94a3b8" : "#7c3aed",
                  color: "white",
                  "&:disabled": { backgroundColor: "#94a3b8", pointerEvents: "none" },
                  display: "flex",
                  alignItems: "center",
                  gap: 0.5,
                  fontSize: "0.6875rem",
                  fontWeight: 500,
                  border: `0.5px solid ${isAnimPromptBlank ? "#e2e8f0" : "#c4b5fd"}`,
                  borderRadius: "6px",
                  padding: "8px 8px",
                  cursor: isAnimPromptBlank || props.isDisabled ? "not-allowed" : "pointer",
                  "&:hover:not(:disabled)": { backgroundColor: "#6115e5" },
                }}
              >
                <SettingsIcon sx={{ fontSize: 11 }} />
                จัดการ Character
              </Box>
            </Box>
          </Tooltip>
        </Box>

        {/* Lock hint — lip-sync ใช้ character ได้โดยไม่ต้องมี Video Prompt จึงไม่ล็อก */}
        {promptRequiredForCharacter && (
          <Typography
            sx={{
              fontSize: "0.625rem",
              color: "#94a3b8",
              mb: 1,
              display: "flex",
              alignItems: "center",
              gap: 0.5,
            }}
          >
            🔒 ต้องกรอก Video Prompt ก่อน
          </Typography>
        )}

        {/* Avatar strip */}
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            gap: 1,
            flexWrap: "wrap",
            opacity: promptRequiredForCharacter ? 0.4 : 1,
            pointerEvents: promptRequiredForCharacter ? "none" : "auto",
          }}
        >
          {/* None */}
          <Box
            onClick={() => props.updateScriptItem(props.index, "character_id", undefined)}
            sx={{
              display: "flex",
              flexDirection: "column",
              alignItems: "center",
              gap: 0.375,
              cursor: "pointer",
              padding: "4px 6px",
              borderRadius: "8px",
              border: selectedCharId === null ? "1.5px solid #7c3aed" : "1.5px solid transparent",
              backgroundColor: selectedCharId === null ? "#f5f3ff" : "transparent",
              "&:hover": { backgroundColor: "#f5f3ff", borderColor: "#c4b5fd" },
              transition: "all 0.15s",
              minWidth: "52px",
            }}
          >
            <Box
              sx={{
                width: 40,
                height: 40,
                borderRadius: "50%",
                border: `1.5px solid ${selectedCharId === null ? "#7c3aed" : "#e2e8f0"}`,
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                backgroundColor: "#f8fafc",
              }}
            >
              <CloseIcon sx={{ fontSize: 14, color: "#94a3b8" }} />
            </Box>
            <Typography
              sx={{
                fontSize: "0.5625rem",
                fontWeight: selectedCharId === null ? 600 : 500,
                color: selectedCharId === null ? "#6d28d9" : "#64748b",
                textAlign: "center",
              }}
            >
              None
            </Typography>
          </Box>

          {/* Character options */}
          {props.characterList.map((char) => {
            const isSelected = selectedCharId === char.id;
            const imgSrc = char.image_url ? getOutputStaticUrl(char.image_url) : null;

            return (
              <Box
                key={char.id}
                onClick={() => props.updateScriptItem(props.index, "character_id", char.id)}
                sx={{
                  display: "flex",
                  flexDirection: "column",
                  alignItems: "center",
                  gap: 0.375,
                  cursor: "pointer",
                  padding: "4px 6px",
                  borderRadius: "8px",
                  border: isSelected ? "1.5px solid #7c3aed" : "1.5px solid transparent",
                  backgroundColor: isSelected ? "#f5f3ff" : "transparent",
                  "&:hover": { backgroundColor: "#f5f3ff", borderColor: "#c4b5fd" },
                  transition: "all 0.15s",
                  minWidth: "52px",
                }}
              >
                <Box
                  sx={{
                    width: 40,
                    height: 40,
                    borderRadius: "50%",
                    border: `1.5px solid ${isSelected ? "#7c3aed" : "#e2e8f0"}`,
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    overflow: "hidden",
                    position: "relative",
                    backgroundColor: "#f5f3ff",
                  }}
                >
                  {imgSrc ? (
                    <Box
                      component="img"
                      src={imgSrc}
                      alt={char.name}
                      sx={{ width: "100%", height: "100%", objectFit: "cover" }}
                    />
                  ) : (
                    <Typography sx={{ fontSize: "1.25rem" }}>👤</Typography>
                  )}
                </Box>
                <Typography
                  sx={{
                    fontSize: "0.5625rem",
                    fontWeight: isSelected ? 600 : 500,
                    color: isSelected ? "#6d28d9" : "#64748b",
                    textAlign: "center",
                    maxWidth: "52px",
                    overflow: "hidden",
                    textOverflow: "ellipsis",
                    whiteSpace: "nowrap",
                  }}
                >
                  {char.name.split(" ")[0]}
                </Typography>
              </Box>
            );
          })}
        </Box>

        {/* Preview card (แสดงเฉพาะตอนเลือกแล้ว) */}
        {selectedChar && (
          <Box
            sx={{
              mt: 1.5,
              display: "flex",
              gap: 1.25,
              backgroundColor: "#f5f3ff",
              border: "0.5px solid #c4b5fd",
              borderRadius: "0.625rem",
              p: 1.25,
            }}
          >
            {/* Thumbnail */}
            <Box
              sx={{
                width: 64,
                height: 64,
                borderRadius: "0.5rem",
                border: "1.5px solid #c4b5fd",
                flexShrink: 0,
                overflow: "hidden",
                backgroundColor: "#ede9fe",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
              }}
            >
              {selectedChar.image_url ? (
                <Box
                  component="img"
                  src={getOutputStaticUrl(selectedChar.image_url)}
                  alt={selectedChar.name}
                  sx={{ width: "100%", height: "100%", objectFit: "cover" }}
                />
              ) : (
                <Typography sx={{ fontSize: "2rem" }}>👤</Typography>
              )}
            </Box>

            {/* Info */}
            <Box sx={{ flex: 1, minWidth: 0 }}>
              <Box sx={{ display: "flex", alignItems: "center", gap: 0.75, mb: 0.375 }}>
                <Typography sx={{ fontSize: "0.8125rem", fontWeight: 500, color: "#4c1d95" }}>
                  {selectedChar.name}
                </Typography>
                <Box
                  sx={{
                    display: "inline-block",
                    px: 0.75,
                    py: "1px",
                    borderRadius: "9999px",
                    fontSize: "0.5rem",
                    fontWeight: 500,
                    backgroundColor: selectedChar.is_validated ? "#dcfce7" : "#fef3c7",
                    color: selectedChar.is_validated ? "#166534" : "#92400e",
                  }}
                >
                  {selectedChar.is_validated ? "validated" : "unverified"}
                </Box>
              </Box>
              <Typography
                sx={{
                  fontSize: "0.625rem",
                  color: "#6d28d9",
                  fontStyle: "italic",
                  mb: 0.75,
                  display: "-webkit-box",
                  WebkitLineClamp: 2,
                  WebkitBoxOrient: "vertical",
                  overflow: "hidden",
                }}
              >
                "{selectedChar.description}"
              </Typography>
              <Box sx={{ display: "flex", alignItems: "center", gap: 0.75 }}>
                <Box
                  sx={{
                    fontSize: "0.5rem",
                    backgroundColor: "#ede9fe",
                    color: "#6d28d9",
                    borderRadius: "9999px",
                    px: 0.75,
                    py: "1px",
                    border: "0.5px solid #c4b5fd",
                  }}
                >
                  {selectedChar.style}
                </Box>
              </Box>
            </Box>
          </Box>
        )}

        {/* Unvalidated warning */}
        {selectedChar && !selectedChar.is_validated && (
          <Box
            sx={{
              mt: 1,
              display: "flex",
              alignItems: "flex-start",
              gap: 0.75,
              p: "6px 10px",
              backgroundColor: "#fef9ee",
              border: "0.5px solid #fcd34d",
              borderRadius: "6px",
            }}
          >
            <Typography sx={{ fontSize: "0.625rem", color: "#b45309" }}>⚠</Typography>
            <Typography sx={{ fontSize: "0.625rem", color: "#b45309" }}>
              Character ยังไม่ผ่าน Validate — กด "จัดการ Character" เพื่อ Validate ก่อนใช้งาน
            </Typography>
          </Box>
        )}

        {/* None hint */}
        {!isAnimPromptBlank && (
          <Typography sx={{ fontSize: "0.625rem", color: "#94a3b8", mt: 0.75 }}>
            กด{" "}
            <Box component="span" sx={{ color: "#6d28d9", fontWeight: 600 }}>
              None
            </Box>{" "}
            เพื่อยกเลิกการเลือก
          </Typography>
        )}
      </Box>

      {/* Video Model */}
      <Box sx={{ mt: 2, pl: 1, pt: 2, borderTop: "1px solid #f1f5f9" }}>
        <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 0.5 }}>
          <FormLabel
            sx={{ flexShrink: 0, color: needsAnimPrompt ? "#94a3b8" : "#6d28d9", fontWeight: 700, mb: 0, whiteSpace: "nowrap" }}
          >
            Video Model
          </FormLabel>
          <Select
            size="small"
            displayEmpty
            value={displayVideoModel}
            onChange={(e) => props.updateScriptItem(props.index, "video_model", e.target.value)}
            disabled={props.isInputDisabled ?? props.isDisabled}
            MenuProps={{
              disablePortal: true,
            }}
            sx={{
              flex: 1,
              borderRadius: "0.5rem",
              fontSize: "0.8rem",
              backgroundColor: "#f8fafc",
              "& .MuiOutlinedInput-notchedOutline": { borderColor: "#e2e8f0" },
              "&:hover .MuiOutlinedInput-notchedOutline": { borderColor: "#c4b5fd" },
              "&.Mui-focused .MuiOutlinedInput-notchedOutline": { borderColor: "#6d28d9" },
              "& .MuiSelect-icon": { color: "#6d28d9" },
            }}
          >
            <MenuItem value="">{videoDefaultLabel(videoModelCatalog)}</MenuItem>
            {enabledVideoModelList.map((model) => (
              <MenuItem key={model.id} value={model.id}>
                {model.label}
              </MenuItem>
            ))}
            {isOrphanVideoModel && (
              <MenuItem key={displayVideoModel} value={displayVideoModel}>
                {`${findVideoModel(displayVideoModel, videoModelCatalog)?.label || displayVideoModel}${videoOrphanSuffix}`}
              </MenuItem>
            )}
          </Select>
        </Box>
        {needsAnimPrompt && (
          <Typography sx={{ fontSize: "10px", color: "#64748b", fontStyle: "italic" }}>
            This model needs a Video Prompt before the scene can be rendered as video.
          </Typography>
        )}
        {!selectedCharId && isLipsyncVideoModel(runningVideoModel, videoModelCatalog) === true && (
          <Typography sx={{ fontSize: "10px", color: "#64748b", fontStyle: "italic" }}>
            No character selected — the scene image will be used as the Lip-Sync face.
          </Typography>
        )}
        {/* สีส้ม (ไม่ใช่เทาเหมือนอีกสองอัน) เพราะอันนี้บอกว่า "ของที่ตั้งไว้จะถูกทิ้ง" ไม่ใช่แค่ให้ข้อมูล */}
        {characterWillBeIgnored && (
          <Typography sx={{ fontSize: "10px", color: "#b45309", fontStyle: "italic" }}>
            This model does not use the selected character — the character will be ignored.
          </Typography>
        )}
      </Box>

      {/* ─── Character Manage Modal ─── */}
      <CharacterManageModal
        open={showModal}
        seriesId={props.seriesId}
        characterList={props.characterList}
        selectedCharacterId={selectedCharId}
        isGenerating={props.isCharacterGenerating}
        onClose={() => setShowModal(false)}
        onSelect={(id) => {
          props.updateScriptItem(props.index, "character_id", id ?? undefined);
          setShowModal(false);
        }}
        onDelete={props.onDeleteCharacter}
        onGenerate={props.onGenerateCharacter}
        onUpload={props.onUploadCharacter}
      />
    </Box>
  );
};

export default CardMeGenSettingsSection;
